import { AGE_GROUP_RANGES, fitsAudience, type Agenda, type AgendaActivity } from '@koderek/shared';
import { t } from '../../texts/pl';

/** Przedział wieku dziecka z grup Z-803 (bez nakładania się granic). */
export type AgeRangeKey = keyof typeof AGE_GROUP_RANGES;
/**
 * Filtr wieku (US-301 AC-3): `person` – zajęcia odpowiednie dla wybranej osoby (dokładnie wg BR-32, Z-301: domyślny,
 * gdy osoba jest wybrana), przedział wieku dziecka, dorośli albo brak.
 */
export type AgeFilter = 'person' | AgeRangeKey | 'adult' | null;
export const AGE_RANGE_KEYS = Object.keys(AGE_GROUP_RANGES) as AgeRangeKey[];

/** Wybrana osoba i rok edycji – do filtra „odpowiednie dla …”. */
export interface FilterPerson {
  name: string;
  kind: 'child' | 'adult';
  birthYear: number | null;
  editionYear: number;
}

/** Zajęcia pasują do przedziału, gdy są dla dzieci i ich zakres wieku zachodzi na przedział (brak granicy = otwarty). */
function fitsAgeFilter(a: AgendaActivity, age: Exclude<AgeFilter, null>, person: FilterPerson | undefined): boolean {
  if (age === 'person') return !person || fitsAudience(a, person, person.editionYear);
  if (age === 'adult') return a.audience !== 'children';
  if (a.audience === 'adults') return false;
  const [min, max] = AGE_GROUP_RANGES[age];
  return (a.ageMin ?? 0) <= max && (a.ageMax ?? 99) >= min;
}

/**
 * BR-31: „Miejsc: wolne/limit”, a po wyczerpaniu limitu „Miejsc z rezerwy: wolne/limit+rezerwa”.
 * `free` = null dla zajęć bez zapisów (BR-22, „Wstęp wolny”).
 */
export function seats(activity: AgendaActivity): { label: string; free: number | null } {
  if (!activity.enrollment) return { label: t.agenda.freeEntry, free: null };
  if (activity.enrolled < activity.capacity) {
    const free = activity.capacity - activity.enrolled;
    return { label: t.agenda.seats(free, activity.capacity), free };
  }
  const total = activity.capacity + activity.reserve;
  const free = Math.max(0, total - activity.enrolled);
  return { label: free ? t.agenda.seatsReserve(free, total) : t.agenda.full, free };
}

export interface AgendaFilters {
  age: AgeFilter;
  typeId: string | null;
  freeOnly: boolean;
}

/** US-301 AC-3: filtry wieku, rodzaju i wolnych miejsc. Odwołane zajęcia zostają widoczne (AC-4). */
export function applyFilters(activities: AgendaActivity[], filters: AgendaFilters, person?: FilterPerson): AgendaActivity[] {
  return activities.filter((a) => {
    if (filters.typeId && a.typeId !== filters.typeId) return false;
    if (filters.freeOnly && (a.status === 'cancelled' || seats(a).free === 0)) return false;
    return filters.age === null || fitsAgeFilter(a, filters.age, person);
  });
}

/** Stoiska i pokazy trwające co najmniej tyle minut mogą trafić do paska „Przez cały dzień” zamiast do kolumn siatki. */
export const ALL_DAY_MIN_MINUTES = 120;

/**
 * Sale do paska „Przez cały dzień”: takie, w których są wyłącznie zajęcia typu nieblokującego czasu (stoisko, pokaz,
 * gra terenowa) trwające co najmniej dwie godziny. W siatce każda z nich zajmowała osobną wąską kolumnę. Sala z choćby
 * jednymi innymi zajęciami zostaje w siatce w całości.
 */
export function allDayRooms(activities: AgendaActivity[], types: Agenda['types']): Set<string> {
  const nonBlocking = new Set(types.filter((type) => !type.blocksTime).map((type) => type.id));
  const qualifies = (a: AgendaActivity) =>
    nonBlocking.has(a.typeId) && (new Date(a.endsAt).getTime() - new Date(a.startsAt).getTime()) / 60_000 >= ALL_DAY_MIN_MINUTES;
  const rooms = new Set(activities.filter(qualifies).map((a) => a.roomId));
  for (const a of activities) if (!qualifies(a)) rooms.delete(a.roomId);
  return rooms;
}

/**
 * Układ treści kafelka w siatce zależnie od wysokości: niski kafelek zaczyna się od symbolu i tytułu (godzinę widać
 * po położeniu), wyższy – od godziny. Resztę pokazuje rozwinięcie po najechaniu lub fokusie.
 */
export function tileLayout(height: number): { titleFirst: boolean; titleLines: number } {
  if (height < 40) return { titleFirst: true, titleLines: 1 };
  if (height < 64) return { titleFirst: true, titleLines: 2 };
  return { titleFirst: false, titleLines: height < 120 ? 2 : 3 };
}
